/**
 * View for the reader review session. Shows a summary of the previous section of text to help the
 * user who may not be sure they understood it.
 */

import $ from "jquery";
import * as preact from "preact";

import * as accountModal from "accountModal";
import BookModel from "models/BookModel";
import models from "models/models";
import readlangClient from "readlangClient";
import * as languageMap from "shared/languageMap";
import * as subscriptionPlans from "shared/subscriptionPlans";
import * as splitTest from "splitTest";

const maxWords = 350;

interface Props {
  bookID: string;
  position: number;
  appRouter: any;
  returnPosition: number;
}

interface State {
  summary?: string;
  badResponse?: boolean;
  secondsSinceFetching: number;
  htmlFragment?: string;
  reachedLimit?: boolean;
}

class ReaderReviewSessionView extends preact.Component<Props, State> {
  /**
   * Timeout ID for the loading timer. This allows us to cancel the timeout upon unmounting the
   * component.
   */
  loadingTimeoutId: number | undefined;

  constructor(props: Props) {
    super(props);

    this.state = { secondsSinceFetching: 0 };

    // Fetch review summary data from server
    this.fetchSession();

    this.runLoadingTimer();

    splitTest.trackEvent("readerReviewOpen");

    if (localStorage.getItem("reader.color") === "colorDark") {
      $("body").addClass("colorDark");
    }

    models.user.on("change:reviewSummaryInEnglish", this.onUserChange);
  }

  onUserChange = async () => {
    if (
      this.state.summary === undefined &&
      this.state.badResponse === undefined
    ) {
      // We're already fetching the summary, so no need to re-fetch
      return;
    }

    await this.setState({
      summary: undefined,
      secondsSinceFetching: 0,
      badResponse: undefined,
    });
    this.fetchSession();
  };

  componentWillUnmount() {
    models.user.off("change:reviewSummaryInEnglish", this.onUserChange);

    if (this.loadingTimeoutId !== undefined) {
      clearTimeout(this.loadingTimeoutId);
    }
  }

  /** This increments `secondsSinceFetching` every second while we are waiting for the response */
  async runLoadingTimer() {
    this.loadingTimeoutId = setTimeout(() => {
      this.setState({
        secondsSinceFetching: this.state.secondsSinceFetching + 1,
      });

      // Call recursively while we're still waiting for the challenges to load
      if (this.state.summary === undefined && !this.state.badResponse) {
        this.runLoadingTimer();
      }
    }, 1000);
  }

  async fetchSession(forceRegenerate = false) {
    if (models.bookModel?.id !== this.props.bookID) {
      models.bookModel = new BookModel({ _id: this.props.bookID });
      await models.bookModel.fetch();
    }

    if (!this.state.htmlFragment) {
      await this.setState({
        htmlFragment: await models.bookModel.getHtmlFragment(
          Math.max(0, this.props.position - maxWords),
          this.props.position
        ),
      });
    }

    try {
      const language = models.bookModel.get("language");
      const { data } = await readlangClient.request(
        "POST",
        "/api/readerReview/generateSummary",
        {
          language: models.bookModel.get("language"),
          text: $(`<div>${this.state.htmlFragment}</div>`).text(),
          bookID: this.props.bookID,
          position: this.props.position,
          forceRegenerate,
          inEnglish:
            language !== "en" && models.user.isReviewSummaryInEnglish(language),
        }
      );

      this.setState({
        summary: data.summary,
      });
    } catch (error) {
      if (error.jqXHR?.status === 403) {
        this.setState({ reachedLimit: true });
        return;
      }
      if (forceRegenerate) {
        this.setState({ badResponse: true });
        splitTest.trackEvent("readerReviewLoadingFailed");
        // Throw error so that we have rollbar logs
        throw error;
      } else {
        // Try again with forceRegenerate=true
        this.fetchSession(true);
      }
    }
  }

  renderCloseButton() {
    return (
      <div
        class="closeButton"
        onClick={() => {
          this.returnToReader();
          splitTest.trackEvent("readerReviewClose");
        }}
      >
        <i class="icon icon-remove" />
      </div>
    );
  }

  render() {
    if (this.state.reachedLimit) {
      return (
        <div class="readerReview">
          {this.renderCloseButton()}
          <h2>Review Limit Reached</h2>
          <p>
            You can only generate one review summary per day on the free plan.
            Please{" "}
            <a
              href="#"
              onClick={() => {
                event.preventDefault();
                event.stopPropagation();
                if (models.user.hasPremiumAccess()) {
                  alert(
                    "You've reached the daily limit of " +
                      subscriptionPlans.permissions.standard.reviewSummaries +
                      " review summaries per day intended to " +
                      "prevent abuse of the system. Contact steve@readlang.com if you think this " +
                      "has happened in error."
                  );
                }
                this.upgrade();
              }}
            >
              upgrade to premium
            </a>{" "}
            for unlimited access.
          </p>
        </div>
      );
    }

    if (this.state.badResponse) {
      return (
        <div class="readerReview">
          {this.renderCloseButton()}
          <h2>Generating Failed</h2>
          <p>There was a problem generating your review session.</p>
          <p>
            <button
              onClick={() => {
                this.setState({ badResponse: false });
                this.fetchSession(true);
              }}
            >
              Try Again
            </button>
          </p>
        </div>
      );
    }

    if (this.state.summary === undefined) {
      return (
        <div class="readerReview">
          {this.renderCloseButton()}
          {
            [
              <p>Generating summary...</p>,
              <p>...shouldn't take too long...</p>,
              <p>...just another few seconds...</p>,
            ][Math.floor(this.state.secondsSinceFetching / 4) % 3]
          }
        </div>
      );
    }

    const language = models.bookModel.get("language");
    const inEnglish =
      language !== "en" && models.user.isReviewSummaryInEnglish(language);

    return (
      <div class="readerReview">
        {this.renderCloseButton()}
        <div>
          <h2>What's going on?</h2>
          <p class="summarySubheading">
            A short summary of the last section, generated by AI
          </p>
          <p class="summaryText">{this.state.summary}</p>
          {language !== "en" ? (
            <p>
              <a
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();

                  models.user.setReviewSummaryToEnglish(language, !inEnglish);
                }}
              >
                Switch summary to{" "}
                {inEnglish ? languageMap.map[language].english : "English"}
              </a>
            </p>
          ) : null}
        </div>
        <div class="footer">
          <button
            class={`primary payButton`}
            onClick={() => {
              this.returnToReader();
            }}
          >
            Return To Text
          </button>
        </div>
      </div>
    );
  }

  upgrade() {
    accountModal.show({
      model: models.user,
      title: "reviewSummariesLimit",
      messageHtml:
        "<div>" +
        "<h2>Upgrade for more than 1 review summary per day</h2>" +
        "<p>Sanity check your understanding with unlimited AI-based " +
        "review summaries and more!</p>" +
        "</div>",
      showTitle: false,
      smartDefinitions: true,
    });
  }

  returnToReader() {
    this.props.appRouter.navigate(
      `/library/${this.props.bookID}/from/${this.props.returnPosition}`,
      {
        trigger: true,
      }
    );
  }
}

export default ReaderReviewSessionView;
